import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Trash2, Car, RotateCcw, Clock, AlertTriangle, FileText, 
  Loader2, ChevronLeft
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { formatDate } from "../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function FleetTrash() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trash, setTrash] = useState({ vehicles: [], contracts: [], documents: [] });
  const [restoring, setRestoring] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, vehicle: null });
  
  const token = localStorage.getItem("token");

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/trash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrash(response.data);
    } catch (err) {
      console.error("Error fetching trash:", err);
      toast.error("Erreur lors du chargement de la corbeille");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTrash();
  }, [fetchTrash]);

  const handleRestore = async (vehicle) => {
    setRestoring(vehicle.id);
    try {
      await axios.post(
        `${API}/fleet/vehicles/${vehicle.id}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Véhicule restauré", {
        description: `${vehicle.brand} ${vehicle.model} a été restauré avec succès`
      });
      fetchTrash();
    } catch (err) {
      console.error("Error restoring vehicle:", err);
      toast.error("Erreur lors de la restauration");
    } finally {
      setRestoring(null);
      setConfirmDialog({ open: false, vehicle: null });
    }
  };

  const getDaysUntilPurge = (deletedAt) => {
    if (!deletedAt) return null;
    const deleted = new Date(deletedAt);
    const purgeDate = new Date(deleted);
    purgeDate.setDate(purgeDate.getDate() + (trash.retention_days || 30));
    const today = new Date();
    const days = Math.ceil((purgeDate - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  const totalItems = trash.vehicles.length + trash.contracts.length + trash.documents.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/fleet/vehicles")}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-lg bg-slate-600 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Corbeille Parc Auto</h1>
              <p className="text-sm text-slate-500">
                {totalItems} élément(s) • Conservation {trash.retention_days || 30} jours
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
        {/* Info Banner */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Éléments supprimés</p>
                <p className="text-sm text-amber-700 mt-1">
                  Les éléments de la corbeille sont automatiquement supprimés définitivement après {trash.retention_days || 30} jours.
                  Vous pouvez restaurer un véhicule à tout moment avant cette date.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Vehicles */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Car className="w-4 h-4 text-indigo-600" />
              Véhicules supprimés
              <Badge variant="outline" className="ml-auto">
                {trash.vehicles.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trash.vehicles.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Trash2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p>Aucun véhicule dans la corbeille</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Véhicule</TableHead>
                    <TableHead>Immatriculation</TableHead>
                    <TableHead>Supprimé le</TableHead>
                    <TableHead>Jours restants</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trash.vehicles.map((vehicle) => {
                    const daysLeft = getDaysUntilPurge(vehicle.deleted_at);
                    return (
                      <TableRow key={vehicle.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                              <Car className="w-4 h-4 text-slate-400" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {vehicle.brand} {vehicle.model}
                              </p>
                              <p className="text-xs text-slate-500">{vehicle.internal_code}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">{vehicle.registration}</TableCell>
                        <TableCell>{formatDate(vehicle.deleted_at)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={daysLeft <= 7 ? "border-red-200 text-red-700 bg-red-50" : ""}
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            {daysLeft} jour(s)
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmDialog({ open: true, vehicle })}
                            disabled={restoring === vehicle.id}
                            data-testid={`restore-vehicle-${vehicle.id}`}
                          >
                            {restoring === vehicle.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restaurer
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        
        {/* Documents */}
        {trash.documents.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Documents supprimés
                <Badge variant="outline" className="ml-auto">
                  {trash.documents.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Véhicule</TableHead>
                    <TableHead>Supprimé le</TableHead>
                    <TableHead>Jours restants</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trash.documents.map((doc) => {
                    const daysLeft = getDaysUntilPurge(doc.deleted_at);
                    return (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span>{doc.custom_label || doc.original_filename}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{doc.vehicle_id}</TableCell>
                        <TableCell>{formatDate(doc.deleted_at)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={daysLeft <= 7 ? "border-red-200 text-red-700 bg-red-50" : ""}
                          >
                            {daysLeft} jour(s)
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Restore Confirmation */}
      <AlertDialog 
        open={confirmDialog.open} 
        onOpenChange={(open) => setConfirmDialog({ open, vehicle: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurer ce véhicule ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le véhicule <strong>{confirmDialog.vehicle?.brand} {confirmDialog.vehicle?.model}</strong> ({confirmDialog.vehicle?.registration}) 
              sera restauré et de nouveau visible dans la liste des véhicules.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => confirmDialog.vehicle && handleRestore(confirmDialog.vehicle)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restaurer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
